import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import { useSession } from '@/hooks/useSession';
import { api } from '@/core/lib/apiClient';
import { Button, MetaRow } from '@/ui';
import type { Person, Source } from './contracts';
import { SourceCard } from './ResourceContent';
import { Card, Field, Form, Row, Small } from './styles';

interface Welcome {
  enabled: boolean;
  canEdit: boolean;
  canManageRoles: boolean;
  owner: Person | null;
  stewards: { person: Person; role: 'admin' | 'moderator' }[];
  revision: number;
  welcome: string;
  tags: string[];
  highlights: Source[];
  guide: { slug: string; title: string } | null;
  nextEvent: { slug: string; title: string; startsAt: string } | null;
}
export function CommunityWelcome({ slug }: { slug: string }) {
  const { currentUser } = useSession();
  return (
    <WelcomeContent
      key={`${currentUser?.id ?? 'guest'}:${slug}`}
      slug={slug}
      owner={currentUser?.id ?? 'guest'}
    />
  );
}
function WelcomeContent({ slug, owner }: { slug: string; owner: string }) {
  const path = `/communities/${encodeURIComponent(slug)}/welcome/`;
  const load = useCallback(() => api<Welcome>(path), [path]);
  const result = useAsyncRead<Welcome | null>(load, owner, null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const d = result.data;
  if (!d?.enabled) return null;
  return (
    <Card>
      <h2>Start here</h2>
      {d.owner && (
        <Small>
          Community owner:{' '}
          <Link to={`/profile/${encodeURIComponent(d.owner.handle)}`}>@{d.owner.handle}</Link>
        </Small>
      )}
      <p>{d.welcome || 'Welcome to the community. Explore its discussions and upcoming events.'}</p>
      <Row>
        {d.tags.map((tag) => (
          <Link
            key={tag}
            to={`/explore?q=${encodeURIComponent(`#${tag}`)}&community=${encodeURIComponent(slug)}`}
          >
            #{tag}
          </Link>
        ))}
      </Row>
      {d.guide && <Link to={`/guides/${encodeURIComponent(d.guide.slug)}`}>{d.guide.title}</Link>}
      {d.highlights.map((s) => (
        <SourceCard key={`${s.postId}:${s.commentId ?? ''}`} source={s} />
      ))}
      {d.nextEvent && (
        <Link to={`/events/${encodeURIComponent(d.nextEvent.slug)}`}>
          <MetaRow>
            {`Next: ${d.nextEvent.title}`}
            {new Date(d.nextEvent.startsAt).toLocaleString()}
          </MetaRow>
        </Link>
      )}
      {error && <p role="alert">{error}</p>}
      {d.canManageRoles && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            if (busy) return;
            const form = e.currentTarget;
            const values = new FormData(form);
            setBusy(true);
            setError('');
            void api(`/communities/${encodeURIComponent(slug)}/roles/`, {
              method: 'POST',
              body: { handle: values.get('handle'), role: values.get('role') },
            })
              .then(
                () => {
                  form.reset();
                  result.retry();
                },
                () =>
                  setError(
                    'Could not update the role. Only the owner may appoint an existing community member. Your input is retained.',
                  ),
              )
              .finally(() => setBusy(false));
          }}
        >
          <h3>Community team</h3>
          {d.stewards.map(({ person, role }) => (
            <Small key={person.id}>
              <MetaRow>
                {`@${person.handle}`}
                {role === 'admin' ? 'Community admin' : 'Moderator'}
              </MetaRow>
            </Small>
          ))}
          <Field>
            Existing member handle
            <input name="handle" required maxLength={80} autoComplete="off" />
          </Field>
          <Field>
            Community role
            <select name="role" defaultValue="moderator">
              <option value="moderator">Moderator</option>
              <option value="admin">Community admin</option>
              <option value="member">Member (remove team role)</option>
            </select>
          </Field>
          <Small>
            Admins and moderators can curate the welcome panel and host community events. Only you
            can appoint the team; these roles do not grant Council powers.
          </Small>
          <Button type="submit" disabled={busy}>
            Update member role
          </Button>
        </Form>
      )}
      {d.canEdit && !editing && (
        <Button variant="ghost" onClick={() => setEditing(true)}>
          Edit welcome guide
        </Button>
      )}
      {editing && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            setBusy(true);
            setError('');
            void api(path, {
              method: 'PATCH',
              body: {
                revision: d.revision,
                welcome: form.get('welcome'),
                tags: String(form.get('tags'))
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
                guideSlug: form.get('guideSlug'),
                highlights: String(form.get('highlights'))
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              },
            })
              .then(
                () => {
                  setEditing(false);
                  result.retry();
                },
                () =>
                  setError(
                    'Could not save. Your input is retained. Check permissions or reload the latest revision.',
                  ),
              )
              .finally(() => setBusy(false));
          }}
        >
          <Field>
            Welcome text
            <textarea name="welcome" defaultValue={d.welcome} maxLength={5000} />
          </Field>
          <Field>
            Topic tags (comma separated)
            <input name="tags" defaultValue={d.tags.join(', ')} maxLength={500} />
          </Field>
          <Field>
            Published guide slug (optional)
            <input name="guideSlug" defaultValue={d.guide?.slug ?? ''} />
          </Field>
          <Field>
            Highlighted post IDs (comma separated, up to five)
            <input name="highlights" defaultValue={d.highlights.map((s) => s.postId).join(', ')} />
          </Field>
          <Small>
            Only community owners and moderators can curate this panel. It does not change Council
            powers or content visibility.
          </Small>
          <Button type="submit" disabled={busy}>
            Save welcome guide
          </Button>
        </Form>
      )}
    </Card>
  );
}
